import {
  PriceCalculatorFormData,
  RouteLeg,
} from "@/features/price-calculator/types/types";
import { replaceAtIndex } from "@/lib/list/ReplaceAtIndex";
import { DateTextInput } from "@stenajs-webui/calendar";
import { Box, Text } from "@stenajs-webui/core";
import { TableCell, TableRow } from "@stenajs-webui/grid";
import * as React from "react";
import { useEffect } from "react";
import { FormModelChangeHandler } from "../../../../../types/forms/types";
import { useSailingsByRouteCodeDepartureDate } from "../../../hooks/UseSailingsByRouteCodeDepartureDate";
import { PriceCalculatorDepartureCategorySelect } from "../common/PriceCalculatorDepartureCategorySelect";
import { PriceCalculatorDepartureTimeInput } from "../common/PriceCalculatorDepartureTimeInput";
import {
  enterManuallyDefaultOption,
  SailingCategoryTimeTableSelect,
} from "../common/PriceCalculatorTimeTableSelect";

interface Props {
  routeLeg: RouteLeg;
  index: number;
  formData: PriceCalculatorFormData;
  onChangeFormData: FormModelChangeHandler<PriceCalculatorFormData>;
  defaultFormState: PriceCalculatorFormData;
}

export const PriceCalculatorLegRow: React.FC<Props> = ({
  routeLeg,
  index,
  formData,
  onChangeFormData,
  defaultFormState,
}) => {
  const { sailings, sailingDeparturesLoading, fetchSailings } =
    useSailingsByRouteCodeDepartureDate();

  const updateRouteLeg = (changes: Partial<RouteLeg>) =>
    onChangeFormData(
      "routeLegs",
      replaceAtIndex(formData.routeLegs ?? [], index, changes)
    );

  useEffect(() => {
    if (
      routeLeg.routeCode &&
      routeLeg.departureDate &&
      routeLeg.departureDate.length > 9 &&
      isNaN(new Date(routeLeg.departureDate).getUTCFullYear()) !== true
    ) {
      fetchSailings({
        variables: {
          routeCode: routeLeg.routeCode,
          departureDate: routeLeg.departureDate,
        },
      });
    }
  }, [routeLeg.routeCode, routeLeg.departureDate, fetchSailings]);

  return (
    <TableRow key={routeLeg.routeCode} alignItems={"center"}>
      <TableCell justifyContent={"flex-start"} width={56}>
        <Text size={"medium"}>{index + 1}</Text>
      </TableCell>
      <TableCell width={64}>
        <Text size={"medium"}>{routeLeg.routeCode}</Text>
      </TableCell>
      <TableCell width={150}>
        <DateTextInput
          value={routeLeg.departureDate}
          onValueChange={(value) =>
            updateRouteLeg({
              departureDate: value,
              sailingTypeCode: defaultFormState.sailingType ?? "",
              departureTime: defaultFormState.departureTime ?? "",
              departureTimeManual: true,
            })
          }
          portalTarget={document.body}
          calendarProps={{
            highlightToday: true,
          }}
        />
      </TableCell>
      <TableCell width={190}>
        <Box width={"100%"}>
          <SailingCategoryTimeTableSelect
            value={
              routeLeg.departureTimeManual
                ? enterManuallyDefaultOption.value
                : routeLeg.sailingTypeCode
            }
            onChangeFormData={onChangeFormData}
            departureTime={routeLeg.departureTime}
            onValueChange={(selectedCategory) => {
              const enterTimeManually =
                selectedCategory.value === enterManuallyDefaultOption.value;

              updateRouteLeg(
                enterTimeManually
                  ? {
                      departureTimeManual: true,
                      sailingTypeCode: defaultFormState.sailingType ?? "",
                      departureTime: defaultFormState.departureTime ?? "",
                    }
                  : {
                      departureTimeManual: false,
                      sailingTypeCode: selectedCategory.value ?? "",
                      departureTime:
                        selectedCategory.departureTime ??
                        defaultFormState.departureTime ??
                        "",
                    }
              );
            }}
            loading={sailingDeparturesLoading}
            sailings={sailings}
          />
        </Box>
      </TableCell>
      <TableCell width={170}>
        <Box width={170}>
          <PriceCalculatorDepartureCategorySelect
            value={routeLeg.sailingTypeCode}
            manualDeparture={routeLeg.departureTimeManual!}
            onValueChange={(sailingCategory) =>
              updateRouteLeg({ sailingTypeCode: sailingCategory.value! })
            }
            disabled={!routeLeg.departureTimeManual}
          />
        </Box>
      </TableCell>
      <TableCell width={140}>
        <PriceCalculatorDepartureTimeInput
          value={!routeLeg.departureTimeManual ? "" : routeLeg.departureTime}
          onChange={(value) => updateRouteLeg({ departureTime: value })}
          disabled={!routeLeg.departureTimeManual}
        />
      </TableCell>
    </TableRow>
  );
};
